import React, { useState } from "react";
import {
  Plus,
  Download,
  ArrowRight,
  ArrowLeft,
  Printer,
  Image,
  Send,
  Copy,
  Upload,
  ExternalLink,
  RefreshCw,
  Trash2,
} from "lucide-react";
import {
  askConfirm,
  Button,
  PageHead,
  SearchBox,
  Status,
  Empty,
  ProductCard,
  ProductImage,
  Field,
  Modal,
  Quantity,
  download,
} from "./ui";
import { api, demo, supabase } from "./data";
import {
  statuses,
  paidStatuses,
  nextStatusAllowed,
  money,
  date,
  csvEncode,
  uid,
} from "./domain";
import { Checkout, ProductDetail } from "./Storefront";
import { receiptPNG } from "./receipts";
export async function sendPendingEmail(orderId) {
  if (demo) return { results: [{ status: "demo" }] };
  const { data } = await supabase.auth.getSession();
  const r = await fetch("/api/mail", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${data.session?.access_token}`,
    },
    body: JSON.stringify({ order_id: orderId }),
  });
  const body = await r.json();
  if (!r.ok) throw new Error(body.error || "No se pudo enviar el correo.");
  return body;
}
export function Orders({ data, run }) {
  const [query, setQuery] = useState(""),
    [status, setStatus] = useState(
      new URLSearchParams(location.hash.split("?")[1] || "").get("estado") ||
        "all",
    ),
    [page, setPage] = useState(1),
    [perPage, setPerPage] = useState(10),
    [channel, setChannel] = useState("all");
  const rows = data.orders.filter(
    (o) =>
      (status === "all" || o.status === status) &&
      (channel === "all" || o.channel === channel) &&
      `${o.customer_name} ${o.number} ${o.phone}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const pages = Math.max(1, Math.ceil(rows.length / perPage));
  const current = Math.min(page, pages);
  function exportOrders() {
    download(
      csvEncode([
        [
          "Pedido",
          "Fecha",
          "Canal",
          "Cliente",
          "Correo",
          "Celular",
          "Estado",
          "Subtotal",
          "Envío",
          "Total",
          "Pago",
          "Fecha de pago",
        ],
        ...rows.map((o) => [
          o.number,
          date(o.created_at),
          o.channel,
          o.customer_name,
          o.email,
          o.phone,
          o.status,
          o.subtotal,
          o.shipping,
          o.total,
          o.payment,
          o.paid_at ? date(o.paid_at) : "",
        ]),
      ]),
      "pedidos-dilo.csv",
      "text/csv;charset=utf-8",
    );
  }
  return (
    <>
      <PageHead
        eyebrow="VENTAS BAJO CONTROL"
        title="Mis pedidos"
        description={`${data.orders.length} pedidos registrados`}
        actions={
          <>
            <Button kind="outline" onClick={exportOrders}>
              <Download size={17} /> Descargar reporte
            </Button>
            <a className="btn" href="#admin/manual">
              <Plus size={17} /> Crear pedido
            </a>
          </>
        }
      />
      <div className="toolbar">
        <SearchBox
          value={query}
          onChange={(v) => {
            setQuery(v);
            setPage(1);
          }}
          placeholder="Buscar cliente, celular o número de pedido"
        />
        <select
          className="control"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
        >
          <option value="all">Todos los canales</option>
          <option value="WEB">Tienda web</option>
          <option value="MANUAL">Pedido manual</option>
        </select>
      </div>
      <div className="card">
        <div className="tabs">
          {["all", ...statuses].map((s) => (
            <button
              key={s}
              className={status === s ? "active" : ""}
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
            >
              {s === "all" ? "Ver todos" : s}{" "}
              <small>
                {
                  data.orders.filter((o) => s === "all" || o.status === s)
                    .length
                }
              </small>
            </button>
          ))}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Número de orden</th>
                <th>Fecha</th>
                <th>Canal</th>
                <th>Colaborador</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Monto</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows
                .slice((current - 1) * perPage, current * perPage)
                .map((o) => (
                  <tr key={o.id}>
                    <td>
                      <a className="row-link" href={`#admin/order/${o.id}`}>
                        {o.number}
                      </a>
                    </td>
                    <td>{date(o.created_at)}</td>
                    <td>
                      <span className="status s-prepedido">{o.channel}</span>
                    </td>
                    <td>
                      <select
                        aria-label={`Colaborador ${o.number}`}
                        className="table-select"
                        value={o.collaborator_id || ""}
                        onChange={(e) =>
                          run(() => api.assign(o.id, e.target.value)).catch(
                            () => {},
                          )
                        }
                      >
                        <option value="">General</option>
                        {data.members.map((m) => (
                          <option key={m.user_id} value={m.user_id}>
                            {m.name || m.email}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <strong>{o.customer_name}</strong>
                    </td>
                    <td>
                      <Status value={o.status} />
                    </td>
                    <td>
                      <strong>{money(o.total)}</strong>
                    </td>
                    <td>
                      <a
                        href={`#admin/order/${o.id}`}
                        className="icon-btn"
                        aria-label={`Abrir pedido ${o.number}`}
                      >
                        <ArrowRight size={17} />
                      </a>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {!rows.length && (
          <Empty
            title="Todavía no hay pedidos aquí"
            description="Las compras de tu tienda y los pedidos manuales aparecerán en esta lista."
          />
        )}
        <div className="pagination">
          <button
            className="icon-btn"
            disabled={current <= 1}
            aria-label="Página anterior"
            onClick={() => setPage(current - 1)}
          >
            <ArrowLeft size={16} />
          </button>
          {current} de {pages}
          <button
            className="icon-btn"
            disabled={current >= pages}
            aria-label="Página siguiente"
            onClick={() => setPage(current + 1)}
          >
            <ArrowRight size={16} />
          </button>
          <select
            aria-label="Pedidos por página"
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n} por página
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
}
export function ManualOrder({ data, run, refresh, notify }) {
  const [query, setQuery] = useState(""),
    [category, setCategory] = useState("all"),
    [product, setProduct] = useState(null),
    [cart, setCart] = useState([]),
    [checkout, setCheckout] = useState(false);
  const products = data.products.filter(
    (p) =>
      p.active &&
      (category === "all" ||
        p.category_id === category ||
        data.categories.find((c) => c.id === p.category_id)?.parent_id ===
          category) &&
      `${p.name} ${p.sku}`.toLowerCase().includes(query.toLowerCase()),
  );
  function add(p, v, q) {
    const old = cart.find((i) => i.variant_id === v.id)?.quantity || 0;
    if (old + q > v.stock) {
      notify("La cantidad supera el stock disponible.", "error");
      return;
    }
    setCart([
      ...cart.filter((i) => i.variant_id !== v.id),
      { variant_id: v.id, quantity: old + q },
    ]);
    setProduct(null);
  }
  return (
    <>
      <PageHead
        eyebrow="VENTA PERSONALIZADA"
        title="Crear pedido manual"
        description="Elige los productos y sus colores. El stock se reserva al confirmar el pedido."
      />
      <div className="manual-grid">
        <div>
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder="Nombre o código del producto"
          />
          <div className="category-pills" style={{ margin: "20px 0" }}>
            <button
              className={category === "all" ? "active" : ""}
              onClick={() => setCategory("all")}
            >
              Todos
            </button>
            {data.categories
              .filter((c) => !c.parent_id)
              .map((c) => (
                <button
                  key={c.id}
                  className={category === c.id ? "active" : ""}
                  onClick={() => setCategory(c.id)}
                >
                  {c.name}
                </button>
              ))}
          </div>
          <div className="products-grid">
            {products.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                variants={data.variants}
                onSelect={setProduct}
                adminActions={
                  <Button
                    kind="outline"
                    className="full"
                    onClick={() => setProduct(p)}
                  >
                    AGREGAR <Plus size={15} />
                  </Button>
                }
              />
            ))}
          </div>
        </div>
        <aside>
          <div className="card manual-summary">
            <h3>
              Resumen de productos{" "}
              <small>{cart.reduce((s, i) => s + i.quantity, 0)}</small>
            </h3>
            {!cart.length ? (
              <Empty
                title="Tu pedido está vacío"
                description="Agrega productos del catálogo."
              />
            ) : (
              cart.map((i) => {
                const v = data.variants.find((v) => v.id === i.variant_id),
                  p = data.products.find((p) => p.id === v.product_id);
                return (
                  <div className="cart-line" key={i.variant_id}>
                    <ProductImage product={p} />
                    <div>
                      <strong>{p.name}</strong>
                      <small>
                        {v.color} · {money(p.price)}
                      </small>
                      <Quantity
                        value={i.quantity}
                        max={v.stock}
                        onChange={(q) =>
                          setCart(
                            cart.map((x) =>
                              x.variant_id === v.id ? { ...x, quantity: q } : x,
                            ),
                          )
                        }
                      />
                    </div>
                    <button
                      className="icon-btn"
                      aria-label={`Quitar ${p.name}`}
                      onClick={() =>
                        setCart(cart.filter((x) => x.variant_id !== v.id))
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })
            )}
            <Button
              className="full"
              style={{ marginTop: 20 }}
              disabled={!cart.length}
              onClick={() => setCheckout(true)}
            >
              Continuar con el cliente <ArrowRight size={17} />
            </Button>
          </div>
        </aside>
      </div>
      {product && (
        <ProductDetail
          product={product}
          data={data}
          onClose={() => setProduct(null)}
          onAdd={add}
        />
      )}
      {checkout && (
        <Checkout
          data={data}
          manual
          items={cart}
          onClose={() => setCheckout(false)}
          onRemove={(id) => setCart(cart.filter((i) => i.variant_id !== id))}
          onQuantity={(id, q) =>
            setCart(
              cart.map((i) =>
                i.variant_id === id ? { ...i, quantity: q } : i,
              ),
            )
          }
          onComplete={async (o) => {
            setCheckout(false);
            setCart([]);
            await refresh();
            location.hash = `#admin/order/${o.id}`;
            sendPendingEmail(o.id).catch(() => {});
            notify("Pedido manual creado");
          }}
        />
      )}
    </>
  );
}
export function OrderDetail({ id, data, run, notify }) {
  const o = data.orders.find((o) => o.id === id);
  const [preview, setPreview] = useState(false),
    [receiptImage, setReceiptImage] = useState(""),
    [busy, setBusy] = useState(false);
  if (!o)
    return (
      <Empty
        title="Pedido no encontrado"
        action={
          <a href="#admin/orders" className="btn">
            Volver a pedidos
          </a>
        }
      />
    );
  const reviewLink = `${location.origin}/#review/${o.review_token}`;
  const tracking = `${location.origin}/#order/${o.tracking_token || o.id}`;
  const jobs = (data.mail_outbox || []).filter((j) => j.order_id === o.id);
  async function status(value) {
    const message =
      value === "Cancelado"
        ? "Se devolverá el stock de este pedido. Si ya hubo pago, la devolución de dinero debe coordinarse manualmente. ¿Cancelar pedido?"
        : paidStatuses.includes(value) && !o.paid_at
          ? "Esto registrará el ingreso. Confirma que ya verificaste el abono. ¿Continuar?"
          : `¿Marcar el pedido como ${value}?`;
    if (!(await askConfirm(message))) return;
    setBusy(true);
    try {
      await run(() => api.setStatus(o.id, value), "Estado actualizado");
      sendPendingEmail(o.id).catch(() => {});
    } catch {
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHead
        eyebrow={o.number}
        title="Detalle de pedido"
        description={`Creado el ${date(o.created_at)}`}
        actions={
          <a className="btn outline" href="#admin/orders">
            <ArrowLeft size={17} /> Mis pedidos
          </a>
        }
      />
      <div className="detail-grid">
        <div className="stack">
          <div className="card">
            <Status value={o.status} />
            <h3 style={{ marginTop: 19 }}>{o.number}</h3>
            <p style={{ fontSize: ".8rem" }}>{date(o.created_at)}</p>
            <div className="totals">
              <p>
                Importe del pedido <strong>{money(o.total)}</strong>
              </p>
              <p>
                Ingreso confirmado{" "}
                <strong>
                  {money(paidStatuses.includes(o.status) ? o.total : 0)}
                </strong>
              </p>
            </div>
            <Field label="Cambiar estado">
              <select
                value={o.status}
                disabled={busy}
                onChange={(e) => status(e.target.value)}
              >
                {statuses.map((s) => (
                  <option
                    value={s}
                    key={s}
                    disabled={!nextStatusAllowed(o.status, s)}
                  >
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            {o.status === "Cancelado" && o.paid_at && (
              <p className="notice">
                Coordina y registra la devolución del dinero por fuera de la
                web.
              </p>
            )}
          </div>
          <div className="card">
            <h3>Información de pago</h3>
            <p>{o.payment}</p>
            {o.paid_at && <small>Confirmado: {date(o.paid_at)}</small>}
            <label
              className="upload-control"
              style={{ marginTop: 16, display: "block" }}
            >
              Adjuntar comprobante de pago
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f)
                    run(async () => {
                      const path = await api.upload(f, data.store.id, true);
                      await api.attachReceipt(o.id, path);
                    }).catch(() => {});
                }}
              />
            </label>
            {o.receipt_url && (
              <Button
                kind="outline"
                className="full"
                onClick={() =>
                  run(async () => {
                    const url = await api.receiptUrl(o.receipt_url);
                    const a = document.createElement("a");
                    a.href = url;
                    a.target = "_blank";
                    a.rel = "noopener noreferrer";
                    a.click();
                  }, "").catch(() => {})
                }
              >
                <ExternalLink size={16} /> Ver archivo adjunto
              </Button>
            )}
          </div>
          <div className="card">
            <h3>Canal de venta</h3>
            <p>{o.channel === "WEB" ? "Tienda web" : "Pedido manual"}</p>
            <Field label="Colaborador">
              <select
                value={o.collaborator_id || ""}
                onChange={(e) =>
                  run(() => api.assign(o.id, e.target.value)).catch(() => {})
                }
              >
                <option value="">General</option>
                {data.members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name || m.email}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>
        <div className="card">
          <div className="actions no-print">
            <Button
              onClick={async () => {
                setPreview(true);
                try {
                  const blob = await receiptPNG(o, data.store, false, false);
                  setReceiptImage(URL.createObjectURL(blob));
                } catch (e) {
                  notify(e.message, "error");
                }
              }}
            >
              <Image size={17} /> Emitir recibo
            </Button>
            <Button
              kind="outline"
              onClick={() => receiptPNG(o, data.store, true)}
            >
              <Download size={17} /> Etiqueta PNG
            </Button>
            <Button kind="outline" onClick={() => window.print()}>
              <Printer size={17} /> Imprimir / PDF
            </Button>
          </div>
          <div className="print-only">
            <h1>{data.store.name} · Recibo</h1>
            <p>
              {o.number} · {o.customer_name} · {date(o.created_at)}
            </p>
            <p>Recibo interno, no fiscal.</p>
          </div>
          <div className="info-grid">
            <section className="card">
              <h3>Resumen del pedido</h3>
              {o.items.map((i, index) => (
                <div className="detail-item" key={i.id || index}>
                  <div>
                    <strong>{i.name}</strong>
                    <small>
                      {i.color} · {i.sku}
                    </small>
                    <small>
                      {i.quantity} × {money(i.unit_price)}
                    </small>
                  </div>
                  <span>{money(i.total)}</span>
                </div>
              ))}
              <div className="totals">
                <p>
                  Subtotal <span>{money(o.subtotal)}</span>
                </p>
                <p>
                  Envío <span>{money(o.shipping)}</span>
                </p>
                <h3>
                  Total <span>{money(o.total)}</span>
                </h3>
              </div>
              {o.notes && <p className="notice">{o.notes}</p>}
            </section>
            <div className="stack">
              <section className="card">
                <h3>Información del cliente</h3>
                <p>{o.customer_name}</p>
                <p>{o.email}</p>
                <p>{o.phone}</p>
                <a
                  className="btn soft no-print"
                  href={`https://wa.me/${o.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hola ${o.customer_name}, te escribimos de Dilo Shop sobre tu pedido ${o.number}.`)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Contactar por WhatsApp <ExternalLink size={16} />
                </a>
              </section>
              <section className="card">
                <h3>Método de entrega</h3>
                <strong>
                  {o.delivery === "pickup" ? "Recojo en tienda" : "Delivery"}
                </strong>
                <p>
                  {o.delivery === "pickup" ? data.store.address : o.address}
                </p>
                <small>Tarifa: {money(o.shipping)}</small>
              </section>
            </div>
          </div>
          <section style={{ marginTop: 28 }}>
            <h3>Historial del pedido</h3>
            <div className="timeline">
              <p>
                Pedido registrado <small>{date(o.created_at)}</small>
              </p>
              {data.events
                .filter((e) => e.order_id === o.id && e.previous_status)
                .sort((a, b) => a.created_at.localeCompare(b.created_at))
                .map((e) => (
                  <p key={e.id}>
                    {e.previous_status} → {e.status}
                    <small>{date(e.created_at)}</small>
                  </p>
                ))}
            </div>
          </section>
          <section className="no-print" style={{ marginTop: 25 }}>
            <h3>Correos y seguimiento</h3>
            {demo ? (
              <p className="notice">
                La demostración no envía correos. El envío se activa al conectar
                Supabase, Cloudflare y el remitente de correo.
              </p>
            ) : (
              <>
                {jobs.map((j) => (
                  <p key={j.id} style={{ fontSize: ".8rem" }}>
                    {j.event === "created" ? "Confirmación de pedido" : j.event}
                    :{" "}
                    <strong>
                      {
                        {
                          sent: "Enviado al proveedor",
                          pending: "Pendiente",
                          failed: "Falló el envío",
                          sending: "Enviando",
                        }[j.status]
                      }
                    </strong>
                    {j.error && <small> · {j.error}</small>}
                  </p>
                ))}
                <Button
                  kind="outline"
                  onClick={() =>
                    run(async () => {
                      const r = await sendPendingEmail(o.id);
                      if (
                        r.results?.some((x) =>
                          ["pending", "failed"].includes(x.status),
                        )
                      )
                        throw new Error(
                          "Correo pendiente. Revisa la configuración del remitente en Integraciones.",
                        );
                    }, "Correos procesados").catch(() => {})
                  }
                >
                  <RefreshCw size={16} /> Reintentar correos pendientes
                </Button>
                <Button
                  kind="outline"
                  onClick={() =>
                    run(
                      () => navigator.clipboard.writeText(tracking),
                      "Enlace privado copiado",
                    ).catch(() => {})
                  }
                >
                  <Copy size={16} /> Copiar seguimiento
                </Button>
              </>
            )}
            {o.status === "Entregado" && (
              <div className="actions" style={{ marginTop: 20 }}>
                <Button
                  kind="outline"
                  onClick={() =>
                    run(
                      () => navigator.clipboard.writeText(reviewLink),
                      "Enlace copiado",
                    ).catch(() => {})
                  }
                >
                  <Copy size={16} /> Copiar enlace de reseña
                </Button>
                <a
                  className="btn soft"
                  href={`https://wa.me/${o.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Gracias por comprar en Dilo Shop. Cuéntanos cómo te fue: ${reviewLink}`)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Send size={16} /> Pedir reseña
                </a>
              </div>
            )}
            {o.review && (
              <p className="notice">
                {"★".repeat(o.review.rating)} · {o.review.text}
              </p>
            )}
          </section>
        </div>
      </div>
      {preview && (
        <Modal
          title={`Recibo ${o.number}`}
          onClose={() => {
            setPreview(false);
            if (receiptImage) URL.revokeObjectURL(receiptImage);
            setReceiptImage("");
          }}
        >
          <div className="receipt-preview">
            {receiptImage ? (
              <>
                <img
                  src={receiptImage}
                  alt={`Recibo ${o.number}`}
                  style={{ width: "100%", border: "1px solid #eee" }}
                />
                <a
                  className="btn full"
                  style={{ marginTop: 20 }}
                  href={receiptImage}
                  download={`recibo-${o.number}.png`}
                >
                  <Download size={17} /> Descargar imagen PNG
                </a>
              </>
            ) : (
              <p>Generando imagen del recibo…</p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
export function Customers({ data }) {
  const [query, setQuery] = useState("");
  const map = new Map();
  for (const o of data.orders) {
    const k = o.email.toLowerCase();
    const c = map.get(k) || {
      name: o.customer_name,
      email: o.email,
      phone: o.phone,
      orders: 0,
      total: 0,
    };
    c.orders++;
    if (paidStatuses.includes(o.status)) c.total += Number(o.total);
    map.set(k, c);
  }
  const customers = [...map.values()].filter((c) =>
    `${c.name} ${c.email} ${c.phone}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  return (
    <>
      <PageHead
        eyebrow="TUS CLIENTES"
        title="Clientes"
        description="Contactos de tus pedidos, reunidos automáticamente."
        actions={
          <Button
            kind="outline"
            onClick={() =>
              download(
                csvEncode([
                  [
                    "Nombre",
                    "Correo",
                    "Celular",
                    "Pedidos",
                    "Compras confirmadas",
                  ],
                  ...customers.map((c) => [
                    c.name,
                    c.email,
                    c.phone,
                    c.orders,
                    c.total,
                  ]),
                ]),
                "clientes-dilo.csv",
                "text/csv;charset=utf-8",
              )
            }
          >
            <Download size={17} /> Exportar
          </Button>
        }
      />
      <div className="toolbar">
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Buscar cliente"
        />
      </div>
      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Correo</th>
              <th>Celular</th>
              <th>Pedidos</th>
              <th>Compras confirmadas</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.email}>
                <td>
                  <strong>{c.name}</strong>
                </td>
                <td>{c.email}</td>
                <td>
                  <a
                    href={`https://wa.me/${c.phone.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {c.phone}
                  </a>
                </td>
                <td>{c.orders}</td>
                <td>{money(c.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!customers.length && <Empty title="Tus clientes aparecerán aquí" />}
      </div>
    </>
  );
}
